/**
 * Formats a date string or object into a compact relative time string.
 * Examples: "1h", "2d", "1w", "1m", "1y"
 * @param date The date to format (string or Date object)
 * @returns Compact relative time string
 */
export function formatCompactTimeAgo(date: string | Date | undefined | null): string {
    if (!date) return "";

    const now = new Date();
    const past = new Date(date);
    const diffInSeconds = Math.round((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return "now";
    }

    const minutes = Math.round(diffInSeconds / 60);
    if (minutes < 60) {
        return `${minutes}m`;
    }

    const hours = Math.round(minutes / 60);
    if (hours < 24) {
        return `${hours}h`;
    }

    const days = Math.round(hours / 24);
    if (days < 7) {
        return `${days}d`;
    }

    const weeks = Math.round(days / 7);
    if (weeks < 4) {
        return `${weeks}w`;
    }

    const months = Math.round(days / 30);
    if (months < 12) {
        return `${months}mo`;
    }

    const years = Math.round(days / 365);
    return `${years}y`;
}

/**
 * Formats a future date into a compact relative time string.
 * Examples: "30d", "1h", "now"
 * @param date The future date
 * @returns Compact relative time string
 */
export function formatCompactTimeRemaining(date: string | Date | undefined | null): string {
    if (!date) return "";
    const now = new Date();
    const future = new Date(date);
    const diffInSeconds = Math.round((future.getTime() - now.getTime()) / 1000);

    if (diffInSeconds <= 0) {
        return "Expired";
    }

    if (diffInSeconds < 60) {
        return "now";
    }

    const minutes = Math.round(diffInSeconds / 60);
    if (minutes < 60) {
        return `${minutes}m`;
    }

    const hours = Math.round(minutes / 60);
    if (hours < 24) {
        return `${hours}h`;
    }

    const days = Math.round(hours / 24);
    if (days < 7) {
        return `${days}d`;
    }

    const weeks = Math.round(days / 7);
    if (weeks < 4) {
        return `${weeks}w`;
    }
    const months = Math.round(days / 30);
    if (months < 12) {
        return `${months} mo`;
    }

    const years = Math.round(days / 365);
    return `${years}y`;
}

/**
 * Adds days to a date.
 * @param date The starting date
 * @param days The number of days to add
 * @returns New Date object
 */
export function addDays(date: string | Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
}
